import React, { useState } from "react";
import { Button as RawButton } from "@/components/ui/button";
import { Card as RawCard } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calender";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  ChevronLeft,
  Calendar as CalendarIcon,
  Clock,
  CheckCircle2,
  Minus,
  Plus,
  Ship,
  ArrowRight,
} from "lucide-react";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { createPageUrl } from "../../utils/index.ts";

/**
 * @typedef {{
 *   ferry: {
 *     image: string,
 *     operator: string,
 *     vessel_type: string,
 *     origin_port: string,
 *     destination_port: string,
 *     duration_minutes: number,
 *     description: string,
 *     fees_included?: string[],
 *     amenities: string[],
 *     available_times: string[],
 *     return_available_times?: string[],
 *     base_price_per_passenger: number,
 *   },
 *   initialDate?: any,
 *   initialReturnDate?: any,
 *   initialPassengers?: number,
 *   isRoundTrip: boolean,
 *   onBack: () => void,
 *   onProceedToCheckout: (selectedDate: any, returnDate: any, passengerCount: number, selectedTime: any, returnSelectedTime: any, arg: string, isRoundTrip: boolean) => void,
 * }} FerryDetailProps
 */

/**
 * @param {FerryDetailProps} props
 */
export default function FerryDetail({
  ferry,
  initialDate,
  initialReturnDate,
  initialPassengers,
  isRoundTrip,
  onBack,
  onProceedToCheckout,
}) {
  const Button = /** @type {React.ComponentType<any>} */ (RawButton);
  const Card = /** @type {React.ComponentType<any>} */ (RawCard);
  const PopoverContentComp = /** @type {React.ComponentType<any>} */ (PopoverContent);

  const [selectedDate, setSelectedDate] = useState(initialDate || null);
  const [returnDate, setReturnDate] = useState(initialReturnDate || null);
  const [passengerCount, setPassengerCount] = useState(initialPassengers || 1);
  const [selectedTime, setSelectedTime] = useState(/** @type {string | null} */ (null));
  const [returnSelectedTime, setReturnSelectedTime] = useState(/** @type {string | null} */ (null));

  const calculateTotal = () => {
    return ferry.base_price_per_passenger * passengerCount * (isRoundTrip ? 2 : 1);
  };

  const canProceed =
    selectedDate && selectedTime && (!isRoundTrip || (returnDate && returnSelectedTime));

  const handleProceed = () => {
    if (canProceed) {
      onProceedToCheckout(
        selectedDate,
        returnDate,
        passengerCount,
        selectedTime,
        returnSelectedTime,
        "none",
        isRoundTrip
      );
    }
  };

  const formattedDuration = `${Math.floor(ferry.duration_minutes / 60)}h ${ferry.duration_minutes % 60}m`;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-gray-100 bg-white sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={onBack}
              className="gap-2 -ml-2 text-[#0B2421] hover:bg-transparent hover:text-[#0B2421]/70 hover:underline"
            >
              <ChevronLeft className="h-4 w-4" />
              Back to Search
            </Button>
            <Link to={createPageUrl("GolfBooking")}>
              <Button
                variant="ghost"
                className="gap-2 text-[#0B2421] hover:bg-transparent hover:text-[#0B2421]/70 hover:underline"
              >
                <ChevronLeft className="h-4 w-4" />
                Back to Golf Booking
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Ferry Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="relative h-[400px] rounded-2xl overflow-hidden shadow-xl"
            >
              <img
                src={ferry.image}
                alt={`${ferry.operator} ferry`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <div className="flex items-center gap-2 mb-3">
                  <Ship className="h-6 w-6" />
                  <span className="text-xl font-semibold">{ferry.operator}</span>
                  <span className="ml-auto bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm">
                    {ferry.vessel_type}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-2xl font-bold">
                  <span>{ferry.origin_port}</span>
                  <ArrowRight className="h-6 w-6" />
                  <span>{ferry.destination_port}</span>
                </div>
                <div className="flex items-center gap-2 mt-2 text-blue-100">
                  <Clock className="h-5 w-5" />
                  <span>Journey Time: {formattedDuration}</span>
                </div>
              </div>
            </motion.div>

            {/* Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
                <h2 className="text-2xl font-bold mb-4 text-[#0B2421]">About This Service</h2>
                <p className="leading-relaxed text-gray-600 whitespace-pre-line">{ferry.description}</p>
              </Card>
            </motion.div>

            {/* Fees Included */}
            {ferry.fees_included && ferry.fees_included.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.15 }}
              >
                <Card className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
                  <h2 className="text-2xl font-bold mb-4 text-[#0B2421]">Included in Fee</h2>
                  <div className="grid grid-cols-2 gap-3">
                    {ferry.fees_included.map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-emerald-600" />
                        <span className="text-gray-600">{item}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              </motion.div>
            )}

            {/* Amenities */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
                <h2 className="text-2xl font-bold mb-4 text-[#0B2421]">Onboard Amenities</h2>
                <div className="grid grid-cols-2 gap-3">
                  {ferry.amenities.map((amenity, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-emerald-600" />
                      <span className="text-gray-600">{amenity}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>
          </div>

          {/* Booking Widget */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="sticky top-24"
            >
              <Card className="p-6 bg-white border border-gray-100 rounded-2xl shadow-md">
                <div className="mb-6">
                  <div className="text-sm mb-1 text-gray-500">Price per passenger</div>
                  <div className="text-4xl font-bold text-[#D4AF37]">
                    SGD {ferry.base_price_per_passenger}
                  </div>
                </div>

                <div className="space-y-5">
                  {/* Departure Date */}
                  <div>
                    <label className="text-sm font-semibold text-[#0B2421] mb-2 block">
                      Departure Date
                    </label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="w-full flex items-center justify-start gap-2 h-12 rounded-lg border border-gray-200 bg-white px-4 text-[#0B2421] hover:bg-gray-50"
                        >
                          <CalendarIcon className="h-4 w-4 text-gray-500" />
                          <span className={selectedDate ? "text-gray-700" : "text-gray-400"}>
                            {selectedDate ? format(selectedDate, "dd MMM yyyy") : "Choose a date"}
                          </span>
                        </button>
                      </PopoverTrigger>
                      <PopoverContentComp className="w-auto p-0" align="start">
                        <Calendar
                          className=""
                          classNames={{}}
                          mode="single"
                          selected={selectedDate}
                          onSelect={setSelectedDate}
                          disabled={/** @param {Date} date */ (date) => date < new Date()}
                        />
                      </PopoverContentComp> 
                    </Popover>
                  </div>

                  {/* Departure Time */}
                  <div>
                    <label className="text-sm font-semibold text-[#0B2421] mb-2 block">
                      Departure Time
                    </label>
                    <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                      {ferry.available_times.map((time) => (
                        <Button
                          key={time}
                          variant={selectedTime === time ? "default" : "outline"}
                          onClick={() => setSelectedTime(time)}
                          className={`h-12 ${
                            selectedTime === time
                              ? "bg-[#0B2421] text-white border-[#0B2421] hover:bg-[#0B2421]"
                              : "text-[#0B2421] bg-gray-50 border-gray-200 hover:bg-[#0B2421] hover:text-white"
                          }`}
                        >
                          <div className="flex flex-col items-center">
                            <Clock className="h-3 w-3 mb-1" />
                            <span className="text-xs">{time}</span>
                          </div>
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Return Date */}
                  {isRoundTrip && (
                    <div>
                      <label className="text-sm font-semibold text-[#0B2421] mb-2 block">
                        Return Date
                      </label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="w-full flex items-center justify-start gap-2 h-12 rounded-lg border border-gray-200 bg-white px-4 text-[#0B2421] hover:bg-gray-50"
                          >
                            <CalendarIcon className="h-4 w-4 text-gray-500" />
                            <span className={returnDate ? "text-gray-700" : "text-gray-400"}>
                              {returnDate ? format(returnDate, "dd MMM yyyy") : "Choose return date"}
                            </span>
                          </button>
                        </PopoverTrigger>
                        <PopoverContentComp className="w-auto p-0" align="start">
                          <Calendar
                            className=""
                            classNames={{}}
                            mode="single"
                            selected={returnDate}
                            onSelect={setReturnDate}
                            disabled={
                              /** @param {Date} date */ (date) =>
                                !selectedDate || date < selectedDate || date < new Date()
                            }
                          />
                        </PopoverContentComp>
                      </Popover>
                    </div>
                  )}

                  {/* Return Time */}
                  {isRoundTrip && (
                    <div>
                      <label className="text-sm font-semibold text-[#0B2421] mb-2 block">
                        Return Time
                      </label>
                      <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                        {(ferry.return_available_times || ferry.available_times).map((time) => (
                          <Button
                            key={`return-${time}`}
                            variant={returnSelectedTime === time ? "default" : "outline"}
                            onClick={() => setReturnSelectedTime(time)}
                            className={`h-12 ${
                              returnSelectedTime === time
                                ? "bg-[#0B2421] text-white border-[#0B2421] hover:bg-[#0B2421]"
                                : "text-[#0B2421] bg-gray-50 border-gray-200 hover:bg-[#0B2421] hover:text-white"
                            }`}
                          >
                            <div className="flex flex-col items-center">
                              <Clock className="h-3 w-3 mb-1" />
                              <span className="text-xs">{time}</span>
                            </div>
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Passenger Selector */}
                  <div>
                    <label className="text-sm font-semibold text-[#0B2421] mb-2 block">
                      Number of Passengers
                    </label>
                    <div className="flex items-center gap-3">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setPassengerCount(Math.max(1, passengerCount - 1))}
                        disabled={passengerCount <= 1}
                        className="h-12 w-12 bg-[#0B2421] hover:bg-[#0B2421]/85 text-white border-none"
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <div className="flex-1 text-center">
                        <div className="text-2xl font-bold text-[#0B2421]">{passengerCount}</div>
                        <div className="text-xs text-gray-500">Passengers</div>
                      </div>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setPassengerCount(Math.min(8, passengerCount + 1))}
                        disabled={passengerCount >= 8}
                        className="h-12 w-12 bg-[#0B2421] hover:bg-[#0B2421]/85 text-white border-none"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="pt-4 border-t border-gray-100 space-y-2">
                    {isRoundTrip && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Trip Type</span>
                        <span className="font-medium text-[#0B2421]">Round-Trip</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Route</span>
                      <span className="font-medium text-[#0B2421] text-right text-xs">
                        {ferry.origin_port} → {ferry.destination_port}
                      </span>
                    </div>
                    {selectedDate && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Departure</span>
                        <span className="font-medium text-[#0B2421]">
                          {format(selectedDate, "dd MMM yyyy")}
                        </span>
                      </div>
                    )}
                    {selectedTime && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Depart Time</span>
                        <span className="font-medium text-[#0B2421]">{selectedTime}</span>
                      </div>
                    )}
                    {isRoundTrip && returnDate && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Return</span>
                        <span className="font-medium text-[#0B2421]">
                          {format(returnDate, "dd MMM yyyy")}
                        </span>
                      </div>
                    )}
                    {isRoundTrip && returnSelectedTime && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Return Time</span>
                        <span className="font-medium text-[#0B2421]">{returnSelectedTime}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">
                        {isRoundTrip ? "Passengers (×2)" : "Passengers"}
                      </span>
                      <span className="font-medium text-[#0B2421]">
                        {passengerCount} × SGD {ferry.base_price_per_passenger}
                        {isRoundTrip ? " × 2" : ""}
                      </span>
                    </div>
                    <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-100">
                      <span className="text-[#0B2421]">Total</span>
                      <span className="text-[#D4AF37]">SGD {calculateTotal()}</span>
                    </div>
                  </div>

                  <Button
                    onClick={handleProceed}
                    disabled={!canProceed}
                    className="w-full h-14 bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold text-lg rounded-xl disabled:opacity-50"
                  >
                    Proceed to Secure Checkout
                  </Button>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}